import { useCreateBrandMutation, useGetAllBrandsLazyQuery } from '@codezo/graphql';
import { toast } from 'sonner';

/** The fields shared by every "create brand" surface. */
export interface BrandDraft {
	name: string;
	description: string;
	website: string;
}

export const emptyBrandDraft: BrandDraft = { name: '', description: '', website: '' };

/** A freshly created brand, so callers can select it right away. */
export interface CreatedBrand {
	id: string;
	name: string;
}

export interface CreateBrandResult {
	success: boolean;
	/** Set when the new id could be resolved. */
	brand?: CreatedBrand;
}

/**
 * Creates a brand for the signed-in organization and resolves its id, so the
 * caller can preselect it. Toasts on success/failure — callers only handle navigation.
 */
export function useCreateBrand() {
	const [createBrandMutation, { loading }] = useCreateBrandMutation();
	const [fetchBrands] = useGetAllBrandsLazyQuery();

	/** createBrand may not return a referenceKey — fall back to looking the brand up by name. */
	const resolveBrandId = async (brandName: string): Promise<string | undefined> => {
		try {
			const { data } = await fetchBrands({
				variables: {
					input: {
						pagination: { limit: 20, currentPage: 1 },
						search: brandName,
					},
				},
				fetchPolicy: 'no-cache',
			});
			const matches = (data?.getAllBrand?.data ?? []).filter((b) => b.name === brandName);
			// Newest wins when several brands share the same name.
			return [...matches].sort(
				(a, b) =>
					new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
			)[0]?.id;
		} catch {
			return undefined;
		}
	};

	const create = async (draft: BrandDraft): Promise<CreateBrandResult> => {
		const name = draft.name.trim();
		if (!name) return { success: false };

		try {
			const { data } = await createBrandMutation({
				variables: {
					input: {
						name,
						description: draft.description.trim() || undefined,
						website: draft.website.trim() || undefined,
					},
				},
			});

			if (!data?.createBrand?.success) {
				toast.error(data?.createBrand?.message || 'Failed to create brand');
				return { success: false };
			}

			const id = data.createBrand.referenceKey ?? (await resolveBrandId(name));
			toast.success('Brand created');
			return { success: true, brand: id ? { id, name } : undefined };
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create brand');
			return { success: false };
		}
	};

	return { create, loading };
}
